import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { useGetProjectQuery, type Project } from "../../app/services/projects";
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import UserList from "./components/UserList";
import ProgressMeter from "../progress-meter/ProgressMeter";
import { useProjectShell } from "../project/ProjectShellContext";

const ANIMATION_DURATION = 1500;

const ProjectBannerStyle = styled.div.attrs<{ $project: Project }>((props) => ({
  className: `banner-color${props.$project?.banner_color || 1} u-col-center u-gap4`,
}))<{ $project: Project; $scrollState: string }>`
    width: 100%;
    height: 75px;
    padding: 0px 16px 0px 16px;

    position: fixed;
    top: ${props => props.$scrollState === "down" ? "0" : "64px"};
    transition: top 0.15s linear;
    
    z-index: 5;
    margin-top: 0;
    box-sizing: border-box;
    
    /* パフォーマンス最適化 */
    will-change: top;
    transform: translateZ(0);
`;

const BannerSpacerStyle = styled.div`
    width: 100%;
    height: 75px;
`;

const CodeNameStyle = styled.div.attrs({
  className: "u-fill"
})<{ $clickable: boolean }>`
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
`;

// AI 生成の透過ロゴ（白単色 PNG）を banner-color の文字色（currentColor）で
// 塗って表示する。フォント表示（CodeNameStyle）の代替。
const BannerLogoStyle = styled.div.attrs({
  className: "u-fill"
})<{ $url: string; $clickable: boolean }>`
  height: 40px;
  background-color: currentColor;
  mask: url("${({ $url }) => $url}") left center / contain no-repeat;
  -webkit-mask: url("${({ $url }) => $url}") left center / contain no-repeat;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
`;

const ProgressMeterContainer = styled.div`
  position: absolute;
  bottom: -6px;
  left: 0;
  width: 100%;
`;

const HoverArea = styled.div`
  position: absolute;
  top: -20px;
  left: 0;
  right: 0;
  bottom: -4px;
  z-index: 1;
`;

const FloatingLabel = styled.div`
  position: absolute;
  top: 6px;
  transform: translateX(-50%);
  white-space: nowrap;
  font-family: "A+mfCv-AXISラウンド 50 L StdN", "M PLUS 1p", sans-serif;
  font-weight: normal;
  font-size: 11px;
  line-height: 1;
  color: #000 !important;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  padding: 9px 13px;
  pointer-events: none;
  z-index: 10;
`;

const formatLabel = (
  pct: number | string,
  approved: number,
  denominator: number,
): string => {
  if (denominator <= 0) return "0.0%（0/0）";
  const fixed = typeof pct === 'number' ? pct.toFixed(1) : pct;
  return `${fixed}%（${approved}/${denominator}）`;
};

const easeOut = (t: number) => 1 - (1 - t) * (1 - t);

interface ProjectBannerProps {
  /** API 取得前・取得失敗時のフォールバック表示用 */
  project?: Project;
}

interface LayoutState {
  layout: string;
}

const ProjectBanner: React.FC<ProjectBannerProps> = ({ project }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const scrollState = useSelector((state: LayoutState) => state.layout);
  // コードネーム / ロゴのタップでプロジェクトの設定を開く（開けるポジションのみ）。
  // 設定を表示中にもう一度タップすると開く前のタブへ戻る。
  const { canOpenSettings, isSettingsOpen, toggleSettings } = useProjectShell();
  
  const { 
    data: currentUserData,
    isLoading: isLoadingUser,
  } = useGetCurrentUserQuery();
  
  const { data: projectData, isLoading, error } = useGetProjectQuery(
    { project_id: projectId ?? "" },
    { 
      skip: !projectId || isLoadingUser || !currentUserData
    }
  );
  
  const currentProject: Project | undefined = projectData?.data?.project || project;

  // 楽観的 approved 加算
  const [optimisticDelta, setOptimisticDelta] = useState(0);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);

  const baseApproved = currentProject?.approved_shots_count ?? 0;
  const baseTotal = currentProject?.total_shots_count ?? 0;
  const baseHold = currentProject?.pending_shots_count ?? 0;
  const baseDenom = Math.max(baseTotal - baseHold, 0);

  // ProgressMeter に渡す楽観的プロジェクトデータ
  const optimisticProject = useMemo(() => {
    if (!currentProject || optimisticDelta === 0) return currentProject;
    return {
      ...currentProject,
      approved_shots_count: baseApproved + optimisticDelta,
    };
  }, [currentProject, optimisticDelta, baseApproved]);

  const displayApproved = baseApproved + optimisticDelta;
  const approvalPercent = useMemo(() => {
    if (baseTotal === 0) return 100;
    if (baseDenom <= 0) return 0;
    return Math.round((displayApproved / baseDenom) * 1000) / 10;
  }, [displayApproved, baseDenom, baseTotal]);

  // ラベル
  const [autoLabel, setAutoLabel] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const prevApprovedRef = useRef<number | null>(null);

  // % カウントアップアニメーション
  const startCountUp = useCallback((
    fromPct: number,
    toPct: number,
    fromApproved: number,
    toApproved: number,
    denom: number,
  ) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / ANIMATION_DURATION, 1);
      const eased = easeOut(t);

      const currentPct = Math.round((fromPct + (toPct - fromPct) * eased) * 10) / 10;
      const currentApproved = Math.round(fromApproved + (toApproved - fromApproved) * eased);
      setAutoLabel(formatLabel(currentPct, currentApproved, denom));

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
        if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
        autoTimerRef.current = setTimeout(() => setAutoLabel(null), 3000);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  // クリック直後: Before ラベル表示 + 楽観的にバーを伸ばす + カウントアップ開始
  const handleApprovalStart = useCallback(() => {
    if (!currentProject || baseDenom <= 0) return;

    const fromPct = baseDenom > 0 ? Math.round((baseApproved / baseDenom) * 1000) / 10 : 0;
    const toPct = baseDenom > 0 ? Math.round(((baseApproved + 1) / baseDenom) * 1000) / 10 : 0;

    setAutoLabel(formatLabel(fromPct, baseApproved, baseDenom));
    setOptimisticDelta(1);

    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    startCountUp(fromPct, toPct, baseApproved, baseApproved + 1, baseDenom);
  }, [currentProject, baseApproved, baseDenom, startCountUp]);

  useEffect(() => {
    window.addEventListener("approval-start", handleApprovalStart);
    return () => window.removeEventListener("approval-start", handleApprovalStart);
  }, [handleApprovalStart]);

  // サーバーデータが追いついたら楽観的デルタをリセット
  useEffect(() => {
    if (!currentProject) return;
    const app = currentProject.approved_shots_count ?? 0;
    const prev = prevApprovedRef.current;

    if (prev !== null && app > prev) {
      setOptimisticDelta(0);
    }
    prevApprovedRef.current = app;
  }, [currentProject]);

  useEffect(() => {
    return () => {
      if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // ホバー時テキスト
  const hoverText = formatLabel(approvalPercent, displayApproved, baseDenom);
  const showLabel = autoLabel || (isHovered ? hoverText : null);
  const labelLeft = `${approvalPercent}%`;

  if (isLoading || error || !currentProject || !optimisticProject) {
    return null;
  }

  const settingsActionLabel = isSettingsOpen
    ? "プロジェクトの設定を閉じる"
    : "プロジェクトの設定を開く";
  const settingsButtonProps = canOpenSettings
    ? {
        role: "button",
        tabIndex: 0,
        "aria-label": settingsActionLabel,
        "aria-pressed": isSettingsOpen,
        onClick: toggleSettings,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleSettings();
          }
        },
      }
    : {};

  return (
    <>
      <ProjectBannerStyle $scrollState={scrollState} $project={currentProject}>
        <div className="u-w100 u-row-between">
          {currentProject.banner_font === 'logo' && currentProject.banner_logo ? (
            <BannerLogoStyle
              $url={currentProject.banner_logo}
              $clickable={canOpenSettings}
              role="img"
              {...settingsButtonProps}
              aria-label={
                canOpenSettings
                  ? `${currentProject.code_name}（${settingsActionLabel}）`
                  : currentProject.code_name
              }
            />
          ) : (
            <CodeNameStyle
              className={`code-font${/^\d+$/.test(String(currentProject.banner_font || '')) ? currentProject.banner_font : 1}`}
              $clickable={canOpenSettings}
              translate="no"
              {...settingsButtonProps}
            >
              {currentProject.code_name}
            </CodeNameStyle>
          )}
          <div style={{ flex: '0 0 38.2%' }}>
            <UserList />
          </div>
        </div>
        <ProgressMeterContainer>
          <div style={{ position: 'relative' }}>
            <ProgressMeter 
              project={optimisticProject}
              variant="banner"
              showDescription={false}
            />
            <HoverArea
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />
            {showLabel && (
              <FloatingLabel style={{ left: labelLeft }}>
                {showLabel}
              </FloatingLabel>
            )}
          </div>
        </ProgressMeterContainer>
      </ProjectBannerStyle>
      <BannerSpacerStyle />
    </>
  );
};

export default ProjectBanner;
